/**
 * The Stripe Custom onboarding flow: the step order, which steps a draft
 * shows, and the NL legal structures. Today only the web wizard reads this;
 * native keeps its own copy (`WizardContainer` + `nlStructures`), and the two
 * already disagree on one rule (a sole proprietorship sees the structure step
 * on native and skips it here). Converge them before trusting either as the
 * flow of record.
 */

export const STRIPE_ONBOARDING_STEPS = [
  "country",
  "work-type",
  "structure",
  "company-details",
  "category-and-mcc",
  "personal-details",
  "beneficial-owners",
  "payout-and-terms",
  "review",
] as const;

/** Reached only after a submit: not part of the progress count. */
export const STRIPE_ONBOARDING_TERMINAL_STEPS = [
  "success",
  "documents",
] as const;

export type StripeOnboardingFormStep = (typeof STRIPE_ONBOARDING_STEPS)[number];
export type StripeOnboardingTerminalStep =
  (typeof STRIPE_ONBOARDING_TERMINAL_STEPS)[number];
export type StripeOnboardingStep =
  StripeOnboardingFormStep | StripeOnboardingTerminalStep;

const ALL_STEPS: readonly string[] = [
  ...STRIPE_ONBOARDING_STEPS,
  ...STRIPE_ONBOARDING_TERMINAL_STEPS,
];

export function isStripeOnboardingStep(
  value: string,
): value is StripeOnboardingStep {
  return ALL_STEPS.includes(value);
}

export type StripeBusinessStructure =
  | "sole_proprietorship"
  | "private_corporation"
  | "public_corporation"
  | "unincorporated_partnership"
  | "incorporated_non_profit"
  | "unincorporated_non_profit";

export interface StripeLegalStructure {
  code: StripeBusinessStructure;
  /** The registry's own name for the entity: a proper noun, the same in every locale. */
  label: string;
  descriptionKey: string;
}

const describe = (code: StripeBusinessStructure) =>
  `stripeOnboarding.structure.options.${code}.description`;

export const NL_COMPANY_STRUCTURES: readonly StripeLegalStructure[] = [
  {
    code: "sole_proprietorship",
    label: "Eenmanszaak",
    descriptionKey: describe("sole_proprietorship"),
  },
  {
    code: "private_corporation",
    label: "BV (Besloten vennootschap)",
    descriptionKey: describe("private_corporation"),
  },
  {
    code: "public_corporation",
    label: "NV (Naamloze vennootschap)",
    descriptionKey: describe("public_corporation"),
  },
  // Stripe's NL taxonomy: VOF / Maatschap is `unincorporated_partnership`.
  {
    code: "unincorporated_partnership",
    label: "VOF / Maatschap",
    descriptionKey: describe("unincorporated_partnership"),
  },
];

export const NL_NONPROFIT_STRUCTURES: readonly StripeLegalStructure[] = [
  {
    code: "incorporated_non_profit",
    label: "Stichting / Vereniging",
    descriptionKey: describe("incorporated_non_profit"),
  },
  {
    code: "unincorporated_non_profit",
    label: "Informele vereniging",
    descriptionKey: describe("unincorporated_non_profit"),
  },
];

export type StripeOnboardingBusinessType = "company" | "non_profit";

/** Only NL is live; an unknown country gets an empty list and the picker says so. */
export function structureOptionsForCountry(
  country: string,
  businessType: StripeOnboardingBusinessType,
): readonly StripeLegalStructure[] {
  if (country !== "NL") return [];
  return businessType === "non_profit"
    ? NL_NONPROFIT_STRUCTURES
    : NL_COMPANY_STRUCTURES;
}

const STRUCTURES_REQUIRING_UBO = new Set<string>([
  "private_corporation",
  "public_corporation",
  "unincorporated_partnership",
]);

/** Same rule as `nativeNlOnboardingSchema`: BV, NV and VOF declare their owners. */
export function structureRequiresUbo(structure: string): boolean {
  return STRUCTURES_REQUIRING_UBO.has(structure);
}

export function structureLabel(code: string): string | null {
  return (
    [...NL_COMPANY_STRUCTURES, ...NL_NONPROFIT_STRUCTURES].find(
      (structure) => structure.code === code,
    )?.label ?? null
  );
}

/**
 * The steps a draft walks, in order. A sole proprietorship skips the
 * structure picker (the work-type step sets it); the owners step exists only
 * for structures that declare owners. Review is always last.
 */
export function computeVisibleSteps(draft: {
  businessType: StripeOnboardingBusinessType;
  companyStructure: string;
}): StripeOnboardingFormStep[] {
  const isSolo =
    draft.businessType === "company" &&
    draft.companyStructure === "sole_proprietorship";
  const showsOwners =
    draft.businessType === "company" &&
    structureRequiresUbo(draft.companyStructure);
  return STRIPE_ONBOARDING_STEPS.filter((step) => {
    if (step === "structure") return !isSolo;
    if (step === "beneficial-owners") return showsOwners;
    return true;
  });
}

/** The step after `current` for this draft, or null on the last one. */
export function nextVisibleStep(
  draft: Parameters<typeof computeVisibleSteps>[0],
  current: StripeOnboardingFormStep,
): StripeOnboardingFormStep | null {
  const steps = computeVisibleSteps(draft);
  const index = steps.indexOf(current);
  return index === -1 ? null : (steps[index + 1] ?? null);
}

/** The step before `current` for this draft, or null on the first one. */
export function previousVisibleStep(
  draft: Parameters<typeof computeVisibleSteps>[0],
  current: StripeOnboardingFormStep,
): StripeOnboardingFormStep | null {
  const steps = computeVisibleSteps(draft);
  const index = steps.indexOf(current);
  return index <= 0 ? null : (steps[index - 1] ?? null);
}
